import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { mockNotifications } from "../../mocks/notifications";
import NotificationsPanel from "../../components/notificationsPanel";
import useAuthStatus from "../../hooks/useAuthStatus";
import { Html5QrcodeScanner } from "html5-qrcode";
import { getAssets, getTransactionHistory, getUser, getUserWallet } from "../../api/apiServices";

// ─── Skeleton primitives ──────────────────────────────────────
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-200 rounded-xl ${className}`} />
);

// Balance card skeleton
function BalanceSkeleton() {
  return (
    <div className="mx-5 mt-4 rounded-2xl p-5 mb-5 overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0F4C35 0%, #1A6B4A 50%, #0F4C35 100%)" }}>
      <div className="mb-1">
        <div className="h-3 w-32 rounded-full bg-white/20 animate-pulse mb-3" />
        <div className="h-9 w-44 rounded-xl bg-white/20 animate-pulse mb-2" />
        <div className="h-3 w-40 rounded-full bg-white/15 animate-pulse mb-6" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <div className="w-11 h-11 rounded-xl bg-white/15 animate-pulse" />
            <div className="h-2 w-10 rounded-full bg-white/15 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Live rates skeleton
function RatesSkeleton() {
  return (
    <div className="flex gap-3 px-5 overflow-x-auto pb-1">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex-shrink-0 bg-white rounded-2xl p-3 min-w-[120px] border border-slate-100 space-y-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-3 w-10" />
          </div>
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-3 w-14" />
        </div>
      ))}
    </div>
  );
}

// Holdings skeleton
function HoldingsSkeleton() {
  return (
    <div className="space-y-2">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="bg-white rounded-2xl px-4 py-3 flex items-center justify-between border border-slate-100">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-2.5 w-16" />
            </div>
          </div>
          <div className="items-end flex flex-col gap-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-2.5 w-10" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Transactions skeleton
function TransactionsSkeleton() {
  return (
    <div className="space-y-2">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-2xl px-4 py-3 flex items-center justify-between border border-slate-100">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-2.5 w-16" />
            </div>
          </div>
          <div className="items-end flex flex-col gap-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-2.5 w-12" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── QR Scanner Modal ─────────────────────────────────────────
function QRScannerModal({ onClose }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("scan");
  const [manualAddress, setManualAddress] = useState("");
  const [scannedAddress, setScannedAddress] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = useRef(null);

  const startScanner = () => {
    if (scannerRef.current) return;
    const scanner = new Html5QrcodeScanner(
      "reader",
      { fps: 10, qrbox: { width: 250, height: 250 }, rememberLastUsedCamera: true },
      false
    );
    scanner.render(
      (decodedText) => {
        setScannedAddress(decodedText);
        setManualAddress(decodedText);
        scanner.clear();
        setIsScanning(false);
      },
      (error) => {
        if (!error?.startsWith("QR code parse error")) console.warn(error);
      }
    );
    scannerRef.current = scanner;
    setIsScanning(true);
  };

  useEffect(() => {
    return () => { if (scannerRef.current) scannerRef.current.clear(); };
  }, []);

  const handleProceed = () => {
    const addressToUse = scannedAddress || manualAddress.trim();
    if (!addressToUse) return;
    onClose();
    navigate("/withdraw-crypto", { state: { recipientAddress: addressToUse } });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "rgba(0,0,0,0.85)" }}>
      <div className="flex items-center justify-between px-5 pt-12 pb-4">
        <button onClick={onClose} className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
          <i className="ri-close-line text-white text-lg" />
        </button>
        <h2 className="text-white font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Scan QR Code</h2>
        <div className="w-9 h-9" />
      </div>
      <div className="flex mx-5 mb-4 bg-white/10 rounded-xl p-1">
        {["scan", "paste"].map(tab => (
          <button key={tab} onClick={() => { setActiveTab(tab); if (tab === "scan" && !isScanning) startScanner(); }}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all capitalize"
            style={{ background: activeTab === tab ? "#fff" : "transparent", color: activeTab === tab ? "#0F4C35" : "#94A3B8" }}>
            {tab === "scan" ? "Scan QR" : "Paste Address"}
          </button>
        ))}
      </div>
      {activeTab === "scan" ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8">
          <div className="relative w-full max-w-xs aspect-square mb-6">
            <div id="reader" className="w-full h-full rounded-2xl overflow-hidden" />
            <div className="absolute inset-0 pointer-events-none">
              {["tl","tr","bl","br"].map(pos => (
                <div key={pos} className={`absolute w-8 h-8 ${pos.includes("t") ? "top-0 border-t-2" : "bottom-0 border-b-2"} ${pos.includes("l") ? "left-0 border-l-2 rounded-tl-2xl" : "right-0 border-r-2 rounded-tr-2xl"} border-emerald-400`}
                  style={{ borderRadius: pos === "tl" ? "16px 0 0 0" : pos === "tr" ? "0 16px 0 0" : pos === "bl" ? "0 0 0 16px" : "0 0 16px 0" }} />
              ))}
            </div>
          </div>
          <p className="text-white/50 text-xs text-center mb-6 max-w-[280px]">Point your camera at a wallet QR code</p>
          {scannedAddress ? (
            <div className="w-full max-w-xs">
              <div className="bg-white/10 rounded-xl px-4 py-3 mb-4">
                <p className="text-white/50 text-xs mb-1">Scanned Address</p>
                <p className="text-white text-xs font-mono break-all">{scannedAddress}</p>
              </div>
              <button onClick={handleProceed} className="w-full py-3.5 rounded-xl font-bold text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">Use This Address</button>
            </div>
          ) : (
            <button onClick={startScanner} className="px-8 py-3 rounded-xl font-bold text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">Start Scanning</button>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col px-5 pt-4">
          <div className="bg-white/10 rounded-2xl p-4 mb-4">
            <p className="text-white/60 text-xs mb-2">Recipient Wallet Address</p>
            <textarea value={manualAddress} onChange={e => setManualAddress(e.target.value)}
              placeholder="Paste or type wallet address here..." rows={4}
              className="w-full bg-transparent text-white text-sm font-mono placeholder-white/30 resize-none outline-none" />
            {manualAddress && <button onClick={() => setManualAddress("")} className="text-white/40 text-xs mt-1">Clear</button>}
          </div>
          <div className="flex gap-2 mb-6">
            <button onClick={async () => { const text = await navigator.clipboard.readText().catch(() => ""); if (text) setManualAddress(text); }}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 hover:bg-white/15 transition-colors">
              <i className="ri-clipboard-line text-white/70" />
              <span className="text-white/70 text-xs font-medium">Paste from Clipboard</span>
            </button>
          </div>
          <button onClick={handleProceed} disabled={!manualAddress.trim()} className="w-full py-3.5 rounded-xl font-bold text-sm transition-all"
            style={{ background: manualAddress.trim() ? "#059669" : "rgba(255,255,255,0.1)", color: manualAddress.trim() ? "#fff" : "rgba(255,255,255,0.4)" }}>
            Continue with Address
          </button>
        </div>
      )}
    </div>
  );
}

export const getHoldingColor = (symbol) => {
  const colors = { BTC: "#F7931A", ETH: "#627EEA", USDT: "#26A17B", BNB: "#F3BA2F", SOL: "#9945FF", XRP: "#00AAE4", DOGE: "#C2A633", ADA: "#0033AD" };
  return colors[symbol] || "#64748B";
};

export const getHoldingIcon = (symbol) => {
  const icons = { BTC: "ri-bitcoin-line", ETH: "ri-eth-line", USDT: "ri-money-dollar-circle-line", BNB: "ri-coin-line", SOL: "ri-star-line" };
  return icons[symbol] || "ri-currency-line";
};

export default function Home() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [balanceVisible, setBalanceVisible] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [assets, setAssets] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [liveRates, setLiveRates] = useState({});
  const [user, setUser] = useState(null);
  const [totalPortfolio, setTotalPortfolio] = useState(0);
  const [nairaBalance, setNairaBalance] = useState(0);
  const [greeting, setGreeting] = useState("");

  // ── Granular loading states ───────────────────────────────
  const [userLoading, setUserLoading] = useState(true);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(true);

  const portfolioInitialLoad = useRef(false);
  const txInitialLoad = useRef(false);
  const ratesInitialLoad = useRef(false);

  const formatNaira = (n) => "₦" + n.toLocaleString("en-NG", { maximumFractionDigits: 2 });

  const parseCurrency = (str) => {
    if (!str) return 0;
    return parseFloat(str.replace(/[₦$,%]/g, ""));
  };

  // Greeting
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good morning");
    else if (hour < 17) setGreeting("Good afternoon");
    else setGreeting("Good evening");
  }, []);

  // User profile
  useEffect(() => {
    const userProfile = async () => {
      setUserLoading(true);
      try {
        const data = await getUser();
        if (data?.status) setUser(data.user);
      } catch (err) {
        console.error("Error fetching user:", err?.response?.data?.message);
      } finally {
        setUserLoading(false);
      }
    };
    userProfile();
  }, []);

 // Assets / live rates
useEffect(() => {
  const fetchAssets = async () => {
    if (!ratesInitialLoad.current) setRatesLoading(true);
    try {
      const data = await getAssets();
      setAssets(data);
    } catch (err) {
      console.error("Fetch failed", err);
    } finally {
      setRatesLoading(false);
      ratesInitialLoad.current = true;
    }
  };
  fetchAssets();
}, []);

// Wallet + portfolio
useEffect(() => {
  if (!isAuthenticated) { setPortfolioLoading(false); return; }

  const fetchAllData = async () => {
    if (!portfolioInitialLoad.current) setPortfolioLoading(true); // ✅ guard only — no duplicate
    try {
      const [walletRes, assetsData] = await Promise.all([getUserWallet(), getAssets()]);
      const rateMap = {};
      assetsData.forEach(asset => {
        rateMap[asset.symbol] = {
          price: parseCurrency(asset.price),
          change: parseCurrency(asset.change),
          icon: asset.icon,
        };
      });
      setLiveRates(rateMap);
      if (walletRes.status === true) {
        setWalletData(walletRes.data);
        setNairaBalance(walletRes.data.nairaBalance || 0);
        let cryptoValue = 0;
        walletRes.data.wallets.forEach(wallet => {
          const rate = rateMap[wallet.currency]?.price || 0;
          cryptoValue += (wallet.balance || 0) * rate;
        });
        setTotalPortfolio((walletRes.data.nairaBalance || 0) + cryptoValue);
      }
    } catch (err) {
      console.error("Failed to load portfolio:", err);
    } finally {
      setPortfolioLoading(false);
      portfolioInitialLoad.current = true; // ✅ flips to true after first fetch
    }
  };

  fetchAllData();
  const interval = setInterval(fetchAllData, 30000);
  return () => clearInterval(interval);
}, [isAuthenticated]);

// Transactions
useEffect(() => {
  if (!isAuthenticated) { setTxLoading(false); return; }

  const fetchTxs = async () => {
    if (!txInitialLoad.current) setTxLoading(true); // ✅ was missing this guard entirely
    try {
      const res = await getTransactionHistory();
      if (res.status === true) setTransactions(res.data);
    } catch (err) {
      console.error("Failed to load transactions", err);
    } finally {
      setTxLoading(false);
      txInitialLoad.current = true; // ✅ was missing this too
    }
  };

  fetchTxs();
}, [isAuthenticated]);

  const realRecentTxs = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    return [...transactions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 4);
  }, [transactions]);

  const realHoldings = useMemo(() => {
    if (!walletData?.wallets || !liveRates) return [];
    return walletData.wallets
      .filter(w => (w.balance || 0) > 0)
      .map(wallet => {
        const assetInfo = liveRates[wallet.currency];
        const rate = assetInfo?.price || 0;
        return {
          symbol: wallet.currency,
          amount: wallet.balance || 0,
          valueNGN: (wallet.balance || 0) * rate,
          change24h: assetInfo?.change || 0,
          iconUrl: assetInfo?.icon || null,
          color: getHoldingColor(wallet.currency),
          icon: getHoldingIcon(wallet.currency),
        };
      })
      .sort((a, b) => b.valueNGN - a.valueNGN);
  }, [walletData, liveRates]);

  const handleProtectedAction = (path) => {
    if (!isAuthenticated) navigate("/auth");
    else navigate(path);
  };

  const txTypeLabel = { buy: "Bought", sell: "Sold", deposit: "Deposited", withdraw_crypto: "Withdrew", withdraw_naira: "Withdrew NGN" };
  const txTypeIcon = { buy: "ri-arrow-down-line", sell: "ri-arrow-up-line", deposit: "ri-arrow-down-circle-line", withdraw_crypto: "ri-arrow-up-circle-line", withdraw_naira: "ri-bank-line" };
  const txTypeColor = { buy: "#059669", sell: "#DC2626", deposit: "#059669", withdraw_crypto: "#D97706", withdraw_naira: "#D97706" };
  const txTypeBg = { buy: "#ECFDF5", sell: "#FEF2F2", deposit: "#ECFDF5", withdraw_crypto: "#FFFBEB", withdraw_naira: "#FFFBEB" };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28">
      {showScanner && <QRScannerModal onClose={() => setShowScanner(false)} />}
      {showNotifications && <NotificationsPanel onClose={() => setShowNotifications(false)} />}

      {/* ── Header ── */}
      <div className="bg-white px-5 pt-12 pb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {userLoading ? (
              <>
                <Skeleton className="w-11 h-11 rounded-full" />
                <div className="space-y-1.5">
                  <Skeleton className="h-2.5 w-20" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </>
            ) : (
              <>
                <div className="w-11 h-11 rounded-full bg-emerald-100 border-2 border-emerald-200 flex items-center justify-center">
                  <span className="text-emerald-700 font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {isAuthenticated ? (user?.firstName?.charAt(0) || "?") : "?"}
                  </span>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">{greeting}</p>
                  <p className="text-slate-800 font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {isAuthenticated ? user?.firstName?.split(" ")[0] : "Guest"} 👋
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <div onClick={() => navigate("/support")} className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center cursor-pointer">
                <i className="ri-customer-service-2-line text-amber-500 text-lg" />
              </div>
            ) : (
              <button onClick={() => navigate("/auth")} className="px-5 py-2 text-sm font-semibold text-emerald-600 border border-emerald-200 rounded-xl hover:bg-emerald-50 transition-colors">
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Balance Card ── */}
{portfolioLoading && isAuthenticated ? (
  <BalanceSkeleton />
) : (
  <div className="mx-4 mt-4 mb-5 rounded-3xl relative overflow-hidden"
    style={{
      background: "linear-gradient(135deg, #0a2e1e 0%, #0f4c35 40%, #1a6b4a 100%)",
      boxShadow: "0 8px 32px rgba(15,76,53,0.35), 0 2px 8px rgba(0,0,0,0.2)"
    }}>

    {/* Auth overlay */}
    {!isAuthenticated && (
      <div className="absolute inset-0 bg-black/65 backdrop-blur-md rounded-3xl flex items-center justify-center z-20">
        <div className="text-center px-6">
          <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-2-line text-white text-2xl" />
          </div>
          <p className="text-white font-semibold mb-1">Sign in to view balance</p>
          <p className="text-emerald-300 text-xs mb-5">Your portfolio is private and secure</p>
          <button onClick={() => navigate("/auth")}
            className="px-6 py-2.5 bg-white text-emerald-700 rounded-2xl font-bold text-sm hover:bg-emerald-50 transition-colors">
            Login to Unlock
          </button>
        </div>
      </div>
    )}

    {/* Decorative mesh circles */}
    <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-[0.07]"
      style={{ background: "radial-gradient(circle, #fff, transparent)", border: "1px solid rgba(255,255,255,0.15)" }} />
    <div className="absolute -bottom-10 -left-10 w-36 h-36 rounded-full opacity-[0.07]"
      style={{ background: "radial-gradient(circle, #fff, transparent)", border: "1px solid rgba(255,255,255,0.1)" }} />
    <div className="absolute top-1/2 right-8 w-20 h-20 rounded-full opacity-[0.05]"
      style={{ background: "radial-gradient(circle, #6ee7b7, transparent)" }} />

    {/* Top accent line */}
    <div className="h-px w-full opacity-20"
      style={{ background: "linear-gradient(90deg, transparent, #6ee7b7 40%, transparent)" }} />

    <div className="relative z-10 p-5">
      {/* Label + eye toggle */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-emerald-300/80 text-xs font-medium tracking-wide">Total Portfolio</span>
        </div>
        {isAuthenticated && (
          <button onClick={() => setBalanceVisible(!balanceVisible)}
            className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
            <i className={`${balanceVisible ? "ri-eye-line" : "ri-eye-off-line"} text-white/60 text-sm`} />
          </button>
        )}
      </div>

      {/* Balance */}
      <div className="mb-5">
        <h2 className="text-white font-bold leading-none mb-1.5"
          style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "2rem" }}>
          {isAuthenticated && balanceVisible ? formatNaira(totalPortfolio) : "₦ ••••••"}
        </h2>
      </div>

      {/* Divider */}
      <div className="h-px mb-4 opacity-15"
        style={{ background: "linear-gradient(90deg, transparent, #fff 30%, #fff 70%, transparent)" }} />

      {/* Action buttons */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "Deposit", icon: "ri-arrow-down-circle-line", path: "/deposit" },
          { label: "Withdraw", icon: "ri-bank-card-line", path: "/withdraw-crypto" },
          { label: "Buy", icon: "ri-swap-box-line", path: "/buy" },
          { label: "Sell", icon: "ri-exchange-dollar-line", path: "/sell" },
        ].map((action) => (
          <button key={action.label}
            onClick={() => handleProtectedAction(action.path)}
            className={`flex flex-col items-center gap-1.5 group ${!isAuthenticated ? "opacity-60" : ""}`}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 group-hover:scale-105 group-active:scale-95"
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(8px)"
              }}>
              <i className={`${action.icon} text-white text-xl`} />
            </div>
            <span className="text-white/60 text-[11px] font-medium">{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  </div>
)}

      {/* ── Live Rates ── */}
      <div className="mb-5">
        <div className="flex items-center justify-between px-5 mb-3">
          <h3 className="text-slate-800 font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Live Rates</h3>
          <button onClick={() => navigate("/buy")} className="text-emerald-600 text-xs font-medium">See all</button>
        </div>

        {ratesLoading ? (
          <RatesSkeleton />
        ) : (
          <div className="flex gap-3 px-5 overflow-x-auto pb-1">
            {assets.map(asset => (
              <button key={asset.symbol} onClick={() => navigate("/buy")}
                className="flex-shrink-0 bg-white rounded-2xl p-3 flex flex-col gap-1 min-w-[120px] cursor-pointer hover:bg-slate-50 transition-colors text-left border border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="text-slate-800 text-xs font-bold">{asset.symbol}</span>
                  <span className={`text-xs font-semibold ${asset.changeType === "positive" ? "text-emerald-600" : "text-red-500"}`}>{asset.change}</span>
                </div>
                <p className="text-slate-400 text-xs">{asset.name}</p>
                <p className="text-slate-700 text-xs font-semibold">{asset.price.toLocaleString()}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── My Holdings ── */}
      <div className="px-5 mb-5 relative">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-slate-800 font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>My Holdings</h3>
          <button onClick={() => handleProtectedAction("/wallet")} className="text-emerald-600 text-xs font-medium">View all</button>
        </div>

        {!isAuthenticated && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-10 flex items-center justify-center rounded-3xl border border-dashed border-slate-300">
            <button onClick={() => navigate("/auth")}
              className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 transition-colors text-white font-semibold rounded-2xl text-sm flex items-center gap-2">
              <i className="ri-login-box-line" />Sign in to see your holdings
            </button>
          </div>
        )}

        <div className={!isAuthenticated ? "blur-sm pointer-events-none" : ""}>
          {portfolioLoading && isAuthenticated ? (
            <HoldingsSkeleton />
          ) : realHoldings.length > 0 ? (
            <div className="space-y-2">
              {realHoldings.slice(0, 3).map((holding) => (
                <div key={holding.symbol} className="bg-white rounded-2xl px-4 py-3 flex items-center justify-between border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" style={{ background: `${holding.color}18` }}>
                      {holding.iconUrl
                        ? <img src={holding.iconUrl} alt={holding.symbol} className="w-7 h-7 object-contain" />
                        : <i className={`${holding.icon} text-lg`} style={{ color: holding.color }} />}
                    </div>
                    <div>
                      <p className="text-slate-800 text-sm font-bold">{holding.symbol}</p>
                      <p className="text-slate-400 text-xs">{holding.amount} {holding.symbol}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-800 text-sm font-bold">₦{holding.valueNGN.toLocaleString()}</p>
                    <p className={`text-xs font-medium ${holding.change24h >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {holding.change24h >= 0 ? "+" : ""}{holding.change24h}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
              <p className="text-slate-400">You don't have any crypto holdings yet.</p>
              <button onClick={() => handleProtectedAction("/deposit")} className="mt-4 text-emerald-600 text-sm font-medium">
                Make your first deposit →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Transactions ── */}
      <div className="px-5 relative">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-slate-800 font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Recent Activity</h3>
          <button onClick={() => handleProtectedAction("/transactions")} className="text-emerald-600 text-xs font-medium">See all</button>
        </div>

        {!isAuthenticated && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-10 flex items-center justify-center rounded-3xl border border-dashed border-slate-300">
            <button onClick={() => navigate("/auth")}
              className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 transition-colors text-white font-semibold rounded-2xl text-sm flex items-center gap-2">
              <i className="ri-login-box-line" />Sign in to view transactions
            </button>
          </div>
        )}

        <div className={!isAuthenticated ? "blur-sm pointer-events-none" : ""}>
          {txLoading && isAuthenticated ? (
            <TransactionsSkeleton />
          ) : realRecentTxs.length > 0 ? (
            <div className="space-y-2">
              {realRecentTxs.map(tx => (
                <div key={tx._id} className="bg-white rounded-2xl px-4 py-3 flex items-center justify-between border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: txTypeBg[tx.type] }}>
                      <i className={`${txTypeIcon[tx.type]} text-lg`} style={{ color: txTypeColor[tx.type] }} />
                    </div>
                    <div>
                      <p className="text-slate-800 text-sm font-semibold">{txTypeLabel[tx.type]} {tx.asset}</p>
                      <p className="text-slate-400 text-xs">
                        {new Date(tx.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}
                        {" · "}
                        <span className={tx.status === "completed" ? "text-emerald-600" : tx.status === "pending" || tx.status === "processing" ? "text-amber-500" : "text-slate-400"}>
                          {tx.status}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-800 text-sm font-bold">
                      {tx.type === "sell" || tx.type === "withdraw_naira" ? "-" : ""}₦{tx.nairaAmount.toLocaleString()}
                    </p>
                    {tx.cryptoAmount > 0 && (
                      <p className="text-slate-400 text-xs">
                        {tx.type === "sell" || tx.type === "withdraw_crypto" ? "-" : "+"}
                        {tx.cryptoAmount} {tx.asset}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
              <p className="text-slate-400">No recent transactions</p>
            </div>
          )}
        </div>
      </div>

      <MobileBottomNav />
    </div>
  );
}
